import type { JSX } from 'react';
import { useStore, type ColorMode } from '@/state/store';
import { Panel, Field } from '@/components/ui/Panel';
import { assignmentColor, ASSIGNMENT_LABEL, CREASE_ASSIGNMENTS } from '@/origami/types';
import { strainColor } from '@/rendering/paperScene';

const COLOR_MODES: { id: ColorMode; label: string; hint: string }[] = [
  { id: 'normal', label: 'Normal', hint: 'Plain paper shading.' },
  { id: 'strain', label: 'Strain', hint: 'Largest principal Green–Lagrange strain per triangle.' },
  { id: 'constraint', label: 'Constraint error', hint: 'Strain, with detected collisions forced to the top of the scale.' },
  { id: 'orientation', label: 'Pieces', hint: 'One hue per connected piece of paper.' },
];

const STRAIN_LEGEND = [0, 0.005, 0.01, 0.02, 0.05];

export function ViewPanel(): JSX.Element {
  const view = useStore((s) => s.view);
  const setView = useStore((s) => s.setView);
  const setDebug = useStore((s) => s.setDebug);
  const snap = useStore((s) => s.snap);
  const setSnap = useStore((s) => s.setSnap);

  return (
    <>
      <Panel title="Display" defaultOpen={false}>
        <Field label="Colour mode">
          <select
            value={view.colorMode}
            onChange={(e) => setView({ colorMode: e.target.value as ColorMode })}
            style={{ width: 132 }}
          >
            {COLOR_MODES.map((m) => (
              <option key={m.id} value={m.id}>
                {m.label}
              </option>
            ))}
          </select>
        </Field>
        <p className="note">{COLOR_MODES.find((m) => m.id === view.colorMode)?.hint}</p>
        {view.colorMode === 'strain' || view.colorMode === 'constraint' ? (
          <div className="legend" style={{ marginTop: 4 }}>
            {STRAIN_LEGEND.map((v) => (
              <span className="chip" key={v}>
                <span
                  className="swatch"
                  style={{ background: `#${strainColor(v).getHexString()}` }}
                />
                {(v * 100).toFixed(v < 0.01 ? 1 : 0)}%
                {v === 0.05 ? '+' : ''}
              </span>
            ))}
          </div>
        ) : null}

        <Field label="Crease lines">
          <input
            type="checkbox"
            checked={view.showCreaseLines}
            onChange={(e) => setView({ showCreaseLines: e.target.checked })}
          />
        </Field>
        <Field label="Wireframe">
          <input
            type="checkbox"
            checked={view.wireframe}
            onChange={(e) => setView({ wireframe: e.target.checked })}
          />
        </Field>
        <Field label="Transparent">
          <input
            type="checkbox"
            checked={view.transparent}
            onChange={(e) => setView({ transparent: e.target.checked })}
          />
        </Field>
        <Field label="Shadows">
          <input
            type="checkbox"
            checked={view.shadows}
            onChange={(e) => setView({ shadows: e.target.checked })}
          />
        </Field>
        <Field label="2D grid">
          <input
            type="checkbox"
            checked={view.showGrid}
            onChange={(e) => setView({ showGrid: e.target.checked })}
          />
        </Field>
        <Field label="Auxiliary edges" title="Triangulation diagonals. They are not creases.">
          <input
            type="checkbox"
            checked={view.showAuxiliary}
            onChange={(e) => setView({ showAuxiliary: e.target.checked })}
          />
        </Field>

        <div className="row" style={{ marginTop: 8 }}>
          <span className="k">Crease legend</span>
        </div>
        <div className="legend">
          {CREASE_ASSIGNMENTS.map((a) => (
            <span className="chip" key={a}>
              <span className="swatch" style={{ background: assignmentColor(a, view.theme) }} />
              {ASSIGNMENT_LABEL[a]}
            </span>
          ))}
        </div>
      </Panel>

      <Panel title="Snapping" defaultOpen={false}>
        <Field label="Enabled">
          <input
            type="checkbox"
            checked={snap.enabled}
            onChange={(e) => setSnap({ enabled: e.target.checked })}
          />
        </Field>
        <Field label="Vertices">
          <input
            type="checkbox"
            checked={snap.vertices}
            onChange={(e) => setSnap({ vertices: e.target.checked })}
          />
        </Field>
        <Field label="Intersections">
          <input
            type="checkbox"
            checked={snap.intersections}
            onChange={(e) => setSnap({ intersections: e.target.checked })}
          />
        </Field>
        <Field label="Midpoints">
          <input
            type="checkbox"
            checked={snap.midpoints}
            onChange={(e) => setSnap({ midpoints: e.target.checked })}
          />
        </Field>
        <Field label="On edges">
          <input
            type="checkbox"
            checked={snap.edges}
            onChange={(e) => setSnap({ edges: e.target.checked })}
          />
        </Field>
        <Field label="Grid">
          <input
            type="checkbox"
            checked={snap.grid}
            onChange={(e) => setSnap({ grid: e.target.checked })}
          />
        </Field>
        <Field label="Grid divisions">
          <input
            type="number"
            min={1}
            max={64}
            step={1}
            value={snap.gridDivisions}
            onChange={(e) => setSnap({ gridDivisions: Math.max(1, Number(e.target.value)) })}
          />
        </Field>
        <Field label="Angle increments">
          <input
            type="checkbox"
            checked={snap.angles}
            onChange={(e) => setSnap({ angles: e.target.checked })}
          />
        </Field>
        <Field label="Angle step (°)">
          <input
            type="number"
            min={1}
            max={90}
            step={1}
            value={snap.angleStepDegrees}
            onChange={(e) => setSnap({ angleStepDegrees: Math.max(1, Number(e.target.value)) })}
          />
        </Field>
      </Panel>

      <Panel title="Debug" defaultOpen={false}>
        {(
          [
            ['vertexIds', 'Vertex IDs'],
            ['edgeIds', 'Edge IDs'],
            ['faceIds', 'Face IDs'],
          ] as const
        ).map(([key, label]) => (
          <Field label={label} key={key}>
            <input
              type="checkbox"
              checked={view.debug[key]}
              onChange={(e) => setDebug({ [key]: e.target.checked })}
            />
          </Field>
        ))}
        <Field label="Performance panel">
          <input
            type="checkbox"
            checked={view.showDebugPanel}
            onChange={(e) => setView({ showDebugPanel: e.target.checked })}
          />
        </Field>
      </Panel>
    </>
  );
}
